import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, X, FloppyDisk } from "@phosphor-icons/react";

const DAYS = [
  { id: "monday", label: "Segunda" },
  { id: "tuesday", label: "Terça" },
  { id: "wednesday", label: "Quarta" },
  { id: "thursday", label: "Quinta" },
  { id: "friday", label: "Sexta" },
  { id: "saturday", label: "Sábado" },
  { id: "sunday", label: "Domingo" },
];

function HourRow({ range, onChange, onRemove }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="time"
        value={range.open}
        onChange={(e) => onChange({ ...range, open: e.target.value })}
        className="px-2 py-1 rounded-full border border-slate-200 text-[12px] focus:border-slate-900 focus:outline-none"
      />
      <span className="text-slate-400 text-[11px]">às</span>
      <input
        type="time"
        value={range.close}
        onChange={(e) => onChange({ ...range, close: e.target.value })}
        className="px-2 py-1 rounded-full border border-slate-200 text-[12px] focus:border-slate-900 focus:outline-none"
      />
      <button
        type="button"
        onClick={onRemove}
        className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-900"
        aria-label="remover"
      >
        <X size={12} weight="bold" />
      </button>
    </div>
  );
}

export default function BusinessHoursTab({ onChange, onToast }) {
  const [weekly, setWeekly] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [tz, setTz] = useState("America/Sao_Paulo");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/operacao/business_hours");
      if (res.data?.configured === false) {
        setWeekly({});
        setHolidays([]);
      } else {
        setWeekly(res.data.weekly || {});
        setHolidays(res.data.holidays || []);
        setTz(res.data.timezone || "America/Sao_Paulo");
      }
    } catch (e) {
      onToast?.("Erro ao carregar horários.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addRange = (day) => {
    setWeekly((prev) => ({
      ...prev,
      [day]: [...(prev[day] || []), { open: "09:00", close: "18:00" }],
    }));
  };

  const updateRange = (day, idx, range) => {
    setWeekly((prev) => ({
      ...prev,
      [day]: prev[day].map((r, i) => (i === idx ? range : r)),
    }));
  };

  const removeRange = (day, idx) => {
    setWeekly((prev) => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== idx),
    }));
  };

  const addHoliday = () => {
    setHolidays([...holidays, { date: "", label: "", closed: true, hours: [] }]);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Strip empty days, validate HH:MM ranges.
      const cleanWeekly = {};
      for (const [day, ranges] of Object.entries(weekly)) {
        const valid = (ranges || []).filter(
          (r) => /^\d{2}:\d{2}$/.test(r.open) && /^\d{2}:\d{2}$/.test(r.close)
        );
        if (valid.length) cleanWeekly[day] = valid;
      }
      const cleanHolidays = holidays.filter((h) => h.date);
      await api.put("/operacao/business_hours", {
        timezone: tz,
        weekly: cleanWeekly,
        holidays: cleanHolidays,
      });
      onToast?.("Horários atualizados.");
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao salvar.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-400">Carregando…</div>;

  return (
    <div className="space-y-8" data-testid="business-hours-tab">
      <div className="border border-slate-100 rounded-2xl overflow-hidden divide-y divide-slate-100">
        {DAYS.map((d) => {
          const ranges = weekly[d.id] || [];
          return (
            <div
              key={d.id}
              data-testid={`hours-day-${d.id}`}
              className="px-5 py-4 grid md:grid-cols-[160px_1fr_auto] items-start gap-4"
            >
              <div className="text-sm font-medium text-slate-900 pt-1.5">{d.label}</div>
              <div className="space-y-1.5">
                {ranges.length === 0 ? (
                  <span className="text-[12px] text-slate-400">Fechado</span>
                ) : (
                  ranges.map((r, i) => (
                    <HourRow
                      key={i}
                      range={r}
                      onChange={(nr) => updateRange(d.id, i, nr)}
                      onRemove={() => removeRange(d.id, i)}
                    />
                  ))
                )}
              </div>
              <button
                type="button"
                onClick={() => addRange(d.id)}
                data-testid={`hours-add-${d.id}`}
                className="px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-900 text-[12px] text-slate-700 inline-flex items-center gap-1 self-start"
              >
                <Plus size={11} weight="bold" /> Faixa
              </button>
            </div>
          );
        })}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-900">Feriados e exceções</h3>
          <button
            type="button"
            onClick={addHoliday}
            data-testid="hours-add-holiday"
            className="px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-[12px] text-slate-700 inline-flex items-center gap-1"
          >
            <Plus size={11} weight="bold" /> Adicionar
          </button>
        </div>
        <div className="space-y-2">
          {holidays.length === 0 && (
            <p className="text-[12px] text-slate-400">Nenhum feriado cadastrado.</p>
          )}
          {holidays.map((h, i) => (
            <div
              key={i}
              className="grid md:grid-cols-[140px_1fr_auto_auto] gap-2 items-center px-3 py-2 rounded-xl border border-slate-100"
              data-testid={`holiday-${i}`}
            >
              <input
                type="date"
                value={h.date}
                onChange={(e) =>
                  setHolidays(holidays.map((x, idx) => idx === i ? { ...x, date: e.target.value } : x))
                }
                className="px-2 py-1 rounded-full border border-slate-200 text-[12px] focus:border-slate-900 focus:outline-none"
              />
              <input
                type="text"
                value={h.label}
                placeholder="Ex.: Natal"
                onChange={(e) =>
                  setHolidays(holidays.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))
                }
                className="px-3 py-1 rounded-full border border-slate-200 text-[12px] focus:border-slate-900 focus:outline-none"
              />
              <label className="text-[12px] text-slate-600 inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={!!h.closed}
                  onChange={(e) =>
                    setHolidays(holidays.map((x, idx) => idx === i ? { ...x, closed: e.target.checked } : x))
                  }
                />
                Fechado
              </label>
              <button
                type="button"
                onClick={() => setHolidays(holidays.filter((_, idx) => idx !== i))}
                className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-900"
              >
                <X size={12} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-4 border-t border-slate-100 flex items-center justify-end">
        <button
          type="button"
          data-testid="hours-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-medium disabled:opacity-50"
        >
          <FloppyDisk size={14} weight="bold" />
          {saving ? "Salvando…" : "Salvar horários"}
        </button>
      </div>
    </div>
  );
}
